import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './UniversalModal.module.scss';

export default function UniversalModal({ isOpen, onClose, title, children }) {
  const contentInnerRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !contentInnerRef.current) return;

    const updateHeight = () => {
      setContentHeight(contentInnerRef.current.scrollHeight);
    };

    updateHeight();

    const ro = new ResizeObserver(updateHeight);
    ro.observe(contentInnerRef.current);

    return () => ro.disconnect();
  }, [isOpen, children]);

  const handleDragEnd = (event, info) => {
    if (info.offset.y > 100 || info.velocity.y > 400) {
      onClose();
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.modalOverlay}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            
            drag={isMobile ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}

            initial={{ y: isMobile ? '100%' : 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: isMobile ? '100%' : 40, opacity: 0 }}
            transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
          >
            <div className={styles.dragHandle}>
              <div className={styles.dragIndicator} />
            </div>

            {title && <h3 className={styles.modalTitle}>{title}</h3>}

            <div
              className={styles.modalContentWrap}
              style={{ height: contentHeight ? `${contentHeight}px` : 'auto' }}
            >
              <div ref={contentInnerRef} className={styles.modalContentInner}>
                {children}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}